import _defineProperty from '@babel/runtime/helpers/esm/defineProperty';
import _extends from '@babel/runtime/helpers/esm/extends';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import React, { forwardRef, useMemo } from 'react';
import { CheckmarkIcon } from '@paypalcorp/pp-react-icons';
import { utilStyles, getColorValue, ColorContext, Shimmer } from '@paypalcorp/pp-react-utils';
import { LoadingSpinner } from '@paypalcorp/pp-react-loading-spinner';
import { css, useTheme, jsx } from '@emotion/react';
import _taggedTemplateLiteral from '@babel/runtime/helpers/esm/taggedTemplateLiteral';
import { math } from 'polished';
import { textStyles } from '@paypalcorp/pp-react-text';

var _templateObject, _templateObject2, _templateObject3, _templateObject4, _templateObject5, _templateObject6, _templateObject7, _templateObject8, _templateObject9, _templateObject10, _templateObject11, _templateObject12, _templateObject13, _templateObject14, _templateObject15, _templateObject16, _templateObject17, _templateObject18, _templateObject19, _templateObject20;

function styles(tokens, colorTokens) {
  /* istanbul ignore next */
  if (!Object.keys(tokens).length) {
    console.warn('Missing Emotion theme. Use ThemeProvider from @emotion/react to pass a valid theme from pp-react.');
    return {};
  }

  var sysSpaceSm = tokens.sysSpaceSm,
      sysSpaceLg = tokens.sysSpaceLg,
      sysSpaceMd = tokens.sysSpaceMd,
      sysSizeIconSm = tokens.sysSizeIconSm,
      sysSizeTapTargetXs = tokens.sysSizeTapTargetXs,
      sysSizeTapTargetSm = tokens.sysSizeTapTargetSm,
      sysSizeCircleLg = tokens.sysSizeCircleLg,
      sysBorderRadiusInteractive = tokens.sysBorderRadiusInteractive,
      sysColorOverlayMain = tokens.sysColorOverlayMain,
      sysColorOverlayContrast = tokens.sysColorOverlayContrast,
      sysColorOverlayMainHover = tokens.sysColorOverlayMainHover,
      sysColorOverlayMainActive = tokens.sysColorOverlayMainActive,
      sysColorPrimaryMain = tokens.sysColorPrimaryMain,
      sysColorPrimaryContrast = tokens.sysColorPrimaryContrast,
      sysColorPrimaryMainHover = tokens.sysColorPrimaryMainHover,
      sysColorPrimaryMainActive = tokens.sysColorPrimaryMainActive,
      sysColorPrimaryContrastActive = tokens.sysColorPrimaryContrastActive,
      sysColorPrimaryContrastHover = tokens.sysColorPrimaryContrastHover,
      sysColorTertiaryMain = tokens.sysColorTertiaryMain,
      sysColorTertiaryContrast = tokens.sysColorTertiaryContrast,
      sysColorTertiaryMainHover = tokens.sysColorTertiaryMainHover,
      sysColorTertiaryMainActive = tokens.sysColorTertiaryMainActive,
      sysColorBackgroundMediumContrast = tokens.sysColorBackgroundMediumContrast,
      sysColorBackgroundHighContrast = tokens.sysColorBackgroundHighContrast,
      sysColorBackgroundSurfaceActive = tokens.sysColorBackgroundSurfaceActive,
      sysColorBackgroundSurfaceHover = tokens.sysColorBackgroundSurfaceHover,
      sysColorSuccessMain = tokens.sysColorSuccessMain,
      sysColorSuccessContrast = tokens.sysColorSuccessContrast;

  var _utilStyles = utilStyles(tokens),
      screenReader = _utilStyles.screenReader,
      focusStyles = _utilStyles.focusStyles,
      shimmerStyles = _utilStyles.shimmerStyles;

  var _textStyles = textStyles(tokens),
      caption = _textStyles.caption;

  var focusOffsetElement = focusStyles.focusOffsetElement;
  var defaults = shimmerStyles.defaults;
  /* token for tap target in small icon-button */

  var sysIconbuttonPositionTapTargetOffset = math("".concat(sysSizeTapTargetSm, " - ").concat(sysSizeTapTargetXs));
  var iconButtonSize = math("".concat(sysSizeIconSm, " + 2 *\n  ").concat(sysSpaceMd));
  var base = css(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n    label: icon-button_base;\n    position: relative;\n    cursor: pointer;\n    border-radius: ", ";\n    padding: ", ";\n    border: none;\n    background-color: transparent;\n    text-decoration: none;\n    display: inline-flex;\n    &:focus {\n      ", ";\n    }\n  "])), sysBorderRadiusInteractive, sysSpaceMd, focusOffsetElement);
  var container = css(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n    label: icon-button_container;\n    position: relative;\n    display: flex;\n    flex-direction: column;\n    align-items: center;\n    padding: ", " 0;\n    width: ", ";\n    word-wrap: break-word;\n    ", "\n    border: none;\n    background-color: transparent;\n\n    &:focus,\n    &:hover {\n      cursor: pointer;\n      -webkit-text-stroke-width: ", ";\n    }\n\n    &:focus {\n      outline: none;\n      & div:first-of-type {\n        ", ";\n      }\n      span:last-of-type {\n        text-decoration: underline;\n      }\n    }\n\n    &:hover {\n      span:last-of-type {\n        text-decoration: underline;\n      }\n    }\n\n    &:hover > div:first-of-type {\n      background: ", ";\n    }\n  "])), sysSpaceMd, '6rem', caption({
    color: getColorValue(colorTokens, sysColorBackgroundHighContrast, 'contrast')
  }), '0.0421875rem', focusOffsetElement, sysColorTertiaryMainHover);
  var processingContainer = css(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n    label: processing_container;\n    &:hover {\n      cursor: default;\n    }\n  "])));
  var successContainer = css(_templateObject4 || (_templateObject4 = _taggedTemplateLiteral(["\n    label: success_container;\n    &:hover {\n      cursor: default;\n    }\n    &:hover > div:first-of-type {\n      background-color: ", ";\n    }\n  "])), getColorValue(colorTokens, sysColorSuccessMain, 'main'));
  var primary = css(_templateObject5 || (_templateObject5 = _taggedTemplateLiteral(["\n    label: primary;\n    color: ", ";\n    background: ", ";\n    &:hover {\n      background: ", ";\n    }\n    &:active {\n      background: ", ";\n    }\n  "])), getColorValue(colorTokens, sysColorPrimaryContrast, 'contrast'), getColorValue(colorTokens, sysColorPrimaryMain, 'main'), getColorValue(colorTokens, sysColorPrimaryMainHover, 'hover'), getColorValue(colorTokens, sysColorPrimaryMainActive, 'active'));
  var inverse = css(_templateObject6 || (_templateObject6 = _taggedTemplateLiteral(["\n    label: inverse;\n    color: ", ";\n  "])), getColorValue(colorTokens, sysColorPrimaryContrast, 'contrast'));
  var sizeSm = css(_templateObject7 || (_templateObject7 = _taggedTemplateLiteral(["\n    label: size_sm;\n    padding: ", ";\n    &:before {\n      content: '';\n      position: absolute;\n      border-radius: ", ";\n      top: -", ";\n      right: -", ";\n      bottom: -", ";\n      left: -", ";\n    }\n  "])), sysSpaceSm, sysBorderRadiusInteractive, math("".concat(sysIconbuttonPositionTapTargetOffset, " / 2")), math("".concat(sysIconbuttonPositionTapTargetOffset, " / 2")), math("".concat(sysIconbuttonPositionTapTargetOffset, " / 2")), math("".concat(sysIconbuttonPositionTapTargetOffset, " / 2")));
  var sizeXl = css(_templateObject8 || (_templateObject8 = _taggedTemplateLiteral(["\n    label: size_xl;\n    ", "\n    padding: ", ";\n  "])), sizeSm, sysSpaceLg);
  var secondary = css(_templateObject9 || (_templateObject9 = _taggedTemplateLiteral(["\n    label: secondary;\n    color: ", ";\n    background: ", ";\n    &:active {\n      background: ", ";\n    }\n    &:hover {\n      background: ", ";\n    }\n  "])), getColorValue(colorTokens, sysColorTertiaryContrast, 'contrast'), getColorValue(colorTokens, sysColorTertiaryMain, 'main'), getColorValue(colorTokens, sysColorTertiaryMainActive, 'active'), getColorValue(colorTokens, sysColorTertiaryMainHover, 'hover'));
  var secondaryInverse = css(_templateObject10 || (_templateObject10 = _taggedTemplateLiteral(["\n    label: secondary_inverse;\n    background: ", ";\n    color: ", ";\n    &:hover {\n      background: ", ";\n    }\n    &:active {\n      background: ", ";\n    }\n  "])), getColorValue(colorTokens, sysColorPrimaryContrast, 'contrast'), getColorValue(colorTokens, sysColorPrimaryMain, 'main'), getColorValue(colorTokens, sysColorPrimaryContrastHover, 'hover'), getColorValue(colorTokens, sysColorPrimaryContrastActive, 'active'));
  var tertiary = css(_templateObject11 || (_templateObject11 = _taggedTemplateLiteral(["\n    label: tertiary;\n    color: ", ";\n    &:hover {\n      background: ", ";\n    }\n    &:active {\n      background: ", ";\n    }\n  "])), getColorValue(colorTokens, sysColorBackgroundMediumContrast, 'contrast'), getColorValue(colorTokens, sysColorBackgroundSurfaceHover, 'hover'), getColorValue(colorTokens, sysColorBackgroundSurfaceActive, 'active'));
  var tertiaryInverse = css(_templateObject12 || (_templateObject12 = _taggedTemplateLiteral(["\n    label: tertiary_inverse;\n    color: ", ";\n    background: ", ";\n    &:hover {\n      background: ", ";\n    }\n    &:active {\n      background: ", ";\n    }\n  "])), getColorValue(colorTokens, sysColorOverlayContrast, 'contrast'), getColorValue(colorTokens, sysColorOverlayMain, 'main'), getColorValue(colorTokens, sysColorOverlayMainHover, 'hover'), getColorValue(colorTokens, sysColorOverlayMainActive, 'active'));
  var icon = css(_templateObject13 || (_templateObject13 = _taggedTemplateLiteral(["\n    label: icon;\n    pointer-events: none;\n  "])));
  var shimmer = css(_templateObject14 || (_templateObject14 = _taggedTemplateLiteral(["\n    label: shimmer;\n    ", "\n  "])), defaults({
    borderRadius: sysBorderRadiusInteractive,
    width: iconButtonSize,
    height: iconButtonSize
  }));
  var shimmerSizeSm = css(_templateObject15 || (_templateObject15 = _taggedTemplateLiteral(["\n    label: shimmer_size_sm;\n    width: ", ";\n    height: ", ";\n    &:before {\n      display: none; //tap-target is not needed in shimmer state\n    }\n  "])), sysSizeTapTargetXs, sysSizeTapTargetXs);
  var shimmerSizeXl = css(_templateObject16 || (_templateObject16 = _taggedTemplateLiteral(["\n    label: shimmer_size_xl;\n    width: ", ";\n    height: ", ";\n    &:before {\n      display: none; //tap-target is not needed in shimmer state\n    }\n  "])), sysSizeCircleLg, sysSizeCircleLg);
  var loadingSpinner = css(_templateObject17 || (_templateObject17 = _taggedTemplateLiteral(["\n    label: loading_spinner;\n    margin: 0;\n  "])));
  var processingState = css(_templateObject18 || (_templateObject18 = _taggedTemplateLiteral(["\n    label: processing_state;\n    pointer-events: none;\n  "])));
  var successState = css(_templateObject19 || (_templateObject19 = _taggedTemplateLiteral(["\n    label: success_state;\n    pointer-events: none;\n    background-color: ", ";\n    color: ", ";\n    &:hover {\n      background: ", ";\n      cursor: default;\n    }\n    &:active {\n      background: ", ";\n    }\n  "])), getColorValue(colorTokens, sysColorSuccessMain, 'main'), getColorValue(colorTokens, sysColorSuccessContrast, 'contrast'), getColorValue(colorTokens, sysColorSuccessMain, 'main'), getColorValue(colorTokens, sysColorSuccessMain, 'main'));
  var label = css(_templateObject20 || (_templateObject20 = _taggedTemplateLiteral(["\n    label: icon-button_label;\n    display: block;\n    text-align: center;\n    padding: ", " ", " 0 ", ";\n    width: inherit;\n    display: -webkit-box;\n    -webkit-line-clamp: 2;\n    -webkit-box-orient: vertical;\n    overflow: hidden;\n  "])), sysSpaceSm, sysSpaceSm, sysSpaceSm);
  return {
    base: base,
    container: container,
    successContainer: successContainer,
    processingContainer: processingContainer,
    primary: primary,
    inverse: inverse,
    sizeSm: sizeSm,
    sizeXl: sizeXl,
    secondary: secondary,
    secondaryInverse: secondaryInverse,
    tertiary: tertiary,
    tertiaryInverse: tertiaryInverse,
    screenReader: screenReader,
    icon: icon,
    shimmer: shimmer,
    shimmerSizeSm: shimmerSizeSm,
    shimmerSizeXl: shimmerSizeXl,
    loadingSpinner: loadingSpinner,
    processingState: processingState,
    successState: successState,
    label: label
  };
}

var _excluded = ["as", "iconName", "svgIconComponent", "className", "label", "iconClassName", "children", "buttonType", "btnState", "screenReaderText", "inverse", "size", "isLoading"];

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }

  return target;
}
/* eslint-disable complexity */


var IconButton = forwardRef(function (props, ref) {
  var as = props.as,
      iconName = props.iconName,
      svgIconComponent = props.svgIconComponent,
      className = props.className,
      label = props.label,
      iconClassName = props.iconClassName,
      children = props.children,
      _props$buttonType = props.buttonType,
      buttonType = _props$buttonType === void 0 ? 'primary' : _props$buttonType,
      _props$btnState = props.btnState,
      btnState = _props$btnState === void 0 ? 'initial' : _props$btnState,
      screenReaderText = props.screenReaderText,
      inverse = props.inverse,
      size = props.size,
      isLoading = props.isLoading,
      rest = _objectWithoutProperties(props, _excluded); // If theme is not directly provided, get from themeContext


  var tokens = useTheme();
  var colorTokens = React.useContext(ColorContext);

  var _styles = useMemo(function () {
    return styles(tokens, colorTokens);
  }, [tokens, colorTokens]);

  if (isLoading) {
    var shimmerSizesStyleMap = {
      sm: _styles.shimmerSizeSm,
      xl: _styles.shimmerSizeXl
    };
    var shimmerCSSVal = [_styles.shimmer, shimmerSizesStyleMap[size]];
    return jsx(Shimmer, _extends({
      css: shimmerCSSVal,
      className: className,
      isInverse: inverse,
      hasStyleDefaults: false
    }, rest, {
      "data-ppui": "true"
    }), children);
  }
  /* istanbul ignore next */


  var iconSize = size === 'xl' ? 'md' : 'sm';
  var sizesStyleMap = {
    sm: _styles.sizeSm,
    xl: _styles.sizeXl
  };
  var cssVal = [_styles.base, sizesStyleMap[size], buttonType === 'primary' && _styles.primary, buttonType === 'secondary' && _styles.secondary, buttonType === 'tertiary' && _styles.tertiary, inverse && _styles.inverse, buttonType === 'secondary' && inverse && _styles.secondaryInverse, buttonType === 'tertiary' && inverse && _styles.tertiaryInverse, btnState === 'success' && _styles.successState, btnState === 'processing' && _styles.processingState];
  var withLabelCssVal = [_styles.base, size === 'sm' ? '' : sizesStyleMap[size], _styles.secondary, btnState === 'success' && _styles.successState, btnState === 'processing' && _styles.processingState];
  var Component = as || 'button';

  if (label) {
    Component = 'div';
  } // Setting type to null will not add the attribute:


  var type = Component === 'button' ? 'button' : null;
  var renderIconComponent = svgIconComponent ? jsx(svgIconComponent, {
    css: _styles.icon,
    className: iconClassName,
    name: iconName,
    size: iconSize,
    "aria-hidden": "true",
    "data-ppui": "true"
  }) : null; // Do not add rest props when there is a label. The `iconButtonComponent` component will be inside a wrapper.

  var restProps = !label ? _objectSpread({}, rest) : {};

  var componentProps = _objectSpread({
    className: className,
    css: label ? withLabelCssVal : cssVal,
    ref: ref,
    type: type,
    disabled: btnState && btnState !== 'initial'
  }, restProps);

  var iconButtonComponent = jsx(Component, _extends({
    "data-ppui-info": "icon-button_3.5.0"
  }, componentProps), btnState !== 'initial' ? function renderButtonState() {
    var iconProps = {
      css: _styles.icon
    };
    var buttonStateComponent = jsx(CheckmarkIcon, _extends({
      size: iconSize
    }, iconProps, {
      "data-ppui": "true"
    }));

    if (btnState === 'processing') {
      /* istanbul ignore next */
      var loadingSpinnerProps = {
        css: [_styles.icon, _styles.loadingSpinner],
        inverse: label ? false : buttonType === 'primary' || buttonType === 'tertiary' && inverse
      };
      buttonStateComponent = jsx(LoadingSpinner, _extends({
        size: iconSize
      }, loadingSpinnerProps, {
        "data-ppui": "true"
      }));
    }

    return jsx(React.Fragment, null, buttonStateComponent, jsx("span", {
      css: _styles.screenReader,
      role: "status",
      "data-ppui": "true"
    }, screenReaderText || btnState));
  }() : jsx(React.Fragment, null, renderIconComponent, !label && jsx("span", {
    css: _styles.screenReader,
    "data-ppui": "true"
  }, children)));

  if (!label) {
    return jsx(React.Fragment, null, iconButtonComponent);
  }

  return jsx(React.Fragment, null, function iconButtonComponentWithLabel() {
    var WrapperComponent = as !== null && as !== void 0 ? as : 'button';
    return jsx(WrapperComponent, _extends({
      css: [_styles.container, btnState === 'success' && _styles.successContainer, btnState === 'processing' && _styles.processingContainer],
      className: className,
      tabIndex: 0
    }, rest, {
      "data-ppui": "true"
    }), iconButtonComponent, jsx("span", {
      css: _styles.label,
      "data-ppui": "true"
    }, label));
  }());
});
export { IconButton, styles as iconButtonStyles };